export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

export interface ApiResponse<T> {
	success: boolean
	data?: T
	message?: string
	error?: string
}

export interface UserProfile {
	id: string
	email: string
	shortName: string
	fullName: string
	gender?: string | null
}

export interface WishItem {
	id: string
	userId: string
	name: string
	description?: string
	url?: string | null
	imageUrl?: string | null
	purchased: boolean
	purchasedBy?: string | null
	createdBy?: string
	isSuggestion?: boolean
	createdAt?: number
	updatedAt?: number
}

export interface Comment {
	id: string
	wishItemId: string
	text: string
	commenterId: string
	visibleToOwner?: boolean
	createdAt?: number
	updatedAt?: number
}

export interface CreateWishItemInput {
	name: string
	description?: string
	createdBy?: string
	url?: string | null
	imageUrl?: string | null
}

export interface UpdateWishItemInput {
	name?: string
	description?: string
	purchased?: boolean
	purchasedBy?: string | null
	url?: string | null
	imageUrl?: string | null
}

export interface CreateCommentInput {
	text: string
	commenterId: string
	visibleToOwner?: boolean
}

export const fetchUserProfile = async (email: string): Promise<UserProfile | null> => {
	if (!email) {
		return null
	}

	const response = await fetch(`${API_BASE_URL}/users?email=${encodeURIComponent(email)}`)

	if (response.status === 404) {
		return null
	}

	if (!response.ok) {
		throw new Error(`Failed to fetch user profile (status ${response.status})`)
	}

	const payload: ApiResponse<UserProfile> = await response.json()

	if (payload.success && payload.data) {
		return payload.data
	}

	return null
}

export const fetchAllUsers = async (): Promise<UserProfile[]> => {
	const response = await fetch(`${API_BASE_URL}/users/`)

	if (response.status === 404) {
		return []
	}

	if (!response.ok) {
		throw new Error(`Failed to fetch users (status ${response.status})`)
	}

	const payload: ApiResponse<UserProfile[]> = await response.json()

	if (payload.success && payload.data) {
		return payload.data
	}

	return []
}

export const createWishItem = async (userId: string, item: CreateWishItemInput): Promise<WishItem> => {
	if (!userId) {
		throw new Error('User identifier is required to create a wish item.')
	}

	const response = await fetch(`${API_BASE_URL}/wishlists/${userId}/items`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			name: item.name.trim(),
			description: item.description?.trim() || undefined,
			createdBy: item.createdBy,
			url: item.url?.trim() || undefined,
			imageUrl: item.imageUrl?.trim() || undefined
		})
	})

	let payload: ApiResponse<WishItem> | null = null
	try {
		payload = await response.json()
	} catch (err) {
		console.error('Failed to parse wish item creation response', err)
	}

	if (!response.ok || !payload?.success || !payload.data) {
		throw new Error(payload?.error ?? `Failed to create wish item (status ${response.status})`)
	}

	return payload.data
}

export const deleteWishItem = async (userId: string, itemId: string): Promise<void> => {
	if (!userId || !itemId) {
		throw new Error('User and item identifiers are required to delete a wish item.')
	}

	const response = await fetch(`${API_BASE_URL}/wishlists/${userId}/items/${itemId}`, {
		method: 'DELETE'
	})

	let payload: ApiResponse<null> | null = null
	try {
		payload = await response.json()
	} catch (err) {
		console.error('Failed to parse wish item deletion response', err)
	}

	if (!response.ok || !payload?.success) {
		throw new Error(payload?.error ?? `Failed to delete wish item (status ${response.status})`)
	}
}

export const updateWishItem = async (userId: string, itemId: string, updates: UpdateWishItemInput): Promise<WishItem> => {
	if (!userId || !itemId) {
		throw new Error('User and item identifiers are required to update a wish item.')
	}

	const requestBody: Record<string, unknown> = {}
	if (typeof updates.name === 'string') {
		requestBody.name = updates.name.trim()
	}
	if (typeof updates.description === 'string') {
		requestBody.description = updates.description.trim()
	}
	if (typeof updates.purchased === 'boolean') {
		requestBody.purchased = updates.purchased
	}
	if ('purchasedBy' in updates) {
		requestBody.purchasedBy = updates.purchasedBy ?? null
	}
	if (typeof updates.url === 'string') {
		requestBody.url = updates.url.trim()
	}
	if ('url' in updates && updates.url === null) {
		requestBody.url = null
	}
	if (typeof updates.imageUrl === 'string') {
		requestBody.imageUrl = updates.imageUrl.trim()
	}
	if ('imageUrl' in updates && updates.imageUrl === null) {
		requestBody.imageUrl = null
	}

	const response = await fetch(`${API_BASE_URL}/wishlists/${userId}/items/${itemId}`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(requestBody)
	})

	let payload: ApiResponse<WishItem> | null = null
	try {
		payload = await response.json()
	} catch (err) {
		console.error('Failed to parse wish item update response', err)
	}

	if (!response.ok || !payload?.success || !payload.data) {
		throw new Error(payload?.error ?? `Failed to update wish item (status ${response.status})`)
	}

	return payload.data
}

export const fetchCommentsForWishList = async (wishListUserId: string, signal?: AbortSignal): Promise<Comment[]> => {
	const trimmedUserId = wishListUserId?.trim()
	if (!trimmedUserId) {
		throw new Error('Wish list user identifier is required to fetch comments.')
	}

	const response = await fetch(`${API_BASE_URL}/comments?wishListUser=${encodeURIComponent(trimmedUserId)}`, {
		signal
	})

	let payload: ApiResponse<Comment[]> | null = null
	try {
		payload = await response.json()
	} catch (err) {
		console.error('Failed to parse wish list comments response', err)
	}

	if (!response.ok || !payload?.success || !Array.isArray(payload.data)) {
		throw new Error(payload?.error ?? `Failed to load comments (status ${response.status})`)
	}

	return payload.data
}

export const createComment = async (wishItemId: string, input: CreateCommentInput): Promise<Comment> => {
	const trimmedItemId = wishItemId?.trim()
	if (!trimmedItemId) {
		throw new Error('Wish item identifier is required to add a comment.')
	}

	const trimmedText = input.text?.trim()
	if (!trimmedText) {
		throw new Error('Comment text is required.')
	}

	const trimmedCommenter = input.commenterId?.trim()
	if (!trimmedCommenter) {
		throw new Error('Commenter identifier is required.')
	}

	const response = await fetch(`${API_BASE_URL}/comments/${trimmedItemId}`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			text: trimmedText,
			commenterId: trimmedCommenter,
			visibleToOwner: input.visibleToOwner ?? true
		})
	})

	let payload: ApiResponse<Comment> | null = null
	try {
		payload = await response.json()
	} catch (err) {
		console.error('Failed to parse comment creation response', err)
	}

	if (!response.ok || !payload?.success || !payload.data) {
		throw new Error(payload?.error ?? `Failed to create comment (status ${response.status})`)
	}

	return payload.data
}
